package org.lennycodes.hldi.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.data.Event
import org.lennycodes.hldi.data.Program
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.lennycodes.hldi.data.BlogPost
import org.lennycodes.hldi.data.Project
import org.lennycodes.hldi.data.TeamMember
import org.lennycodes.hldi.pages.admin.shortBio
import org.lennycodes.hldi.styles.BlogCardStyle
import org.lennycodes.hldi.styles.ButtonStyle
import org.lennycodes.hldi.styles.ProgramCardStyle
import org.lennycodes.hldi.styles.TeamCardBioStyle
import org.lennycodes.hldi.styles.TeamCardContentStyle
import org.lennycodes.hldi.styles.TeamCardDepartmentStyle
import org.lennycodes.hldi.styles.TeamCardImageStyle
import org.lennycodes.hldi.styles.TeamCardImageWrapperStyle
import org.lennycodes.hldi.styles.TeamCardLeadershipBadgeStyle
import org.lennycodes.hldi.styles.TeamCardNameStyle
import org.lennycodes.hldi.styles.TeamCardRoleStyle
import org.lennycodes.hldi.styles.TeamCardRoleUnderlineStyle
import org.lennycodes.hldi.styles.TeamCardStyle
import org.lennycodes.hldi.styles.ViewProfileButtonStyle

@Composable
fun ProgramCard(program: Program) {
    Div(
        attrs = Modifier.then(ProgramCardStyle.toModifier()).toAttrs()

    ) {
        // Icon
        Div(
            attrs = {
                style {
                    fontSize(48.px)
                    marginBottom(16.px)
                }
            }
        ) {
            Text(program.icon)
        }

        // Title
        H3(
            attrs = {
                style {
                    fontSize(20.px)
                    color(HLDITheme.Colors.primary)
                    marginBottom(12.px)
                    fontFamily("Playfair Display", "serif")
                }
            }
        ) {
            Text(program.title)
        }

        // Description
        P(
            attrs = {
                style {
                    fontSize(14.px)
                    color(HLDITheme.Colors.mediumGray)
                    lineHeight("1.6")
                    marginBottom(24.px)
                }
            }
        ) {
            Text(program.description.shortBio(limit = 200))
        }

        // CTA
        GradientButton(
            text = "Learn More",
            href = "/programs#${program.id}",
            modifier = Modifier.then(ButtonStyle.toModifier())
        )
    }
}

@Composable
fun ProjectCard(project: Project) {
    Div(
        attrs = Modifier.then(ProgramCardStyle.toModifier())
            .toAttrs()
    ) {
        // Image
        Div(
            attrs = {
                style {
                    height(200.px)
                    backgroundColor(HLDITheme.Colors.accent)
                    overflow(Overflow.Hidden)
                }
            }
        ) {
            Img(
                src = project.imageUrl,
                alt = project.title,
                attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        objectFit(ObjectFit.Cover)
                        transition(
                            Transition.of(
                                property = "all",
                                duration = 400.ms,
                                timingFunction = TransitionTimingFunction.Ease
                            )
                        )
                    }
                }
            )
        }

        // Content
        Div(
            attrs = {
                style {
                    padding(24.px)
                }
            }
        ) {
            H3(
                attrs = {
                    style {
                        fontSize(18.px)
                        color(HLDITheme.Colors.text)
                        marginBottom(8.px)
                        fontFamily("Playfair Display", "serif")
                    }
                }
            ) {
                Text(project.title)
            }

            Div(
                attrs = {
                    style {
                        fontSize(14.px)
                        color(HLDITheme.Colors.mediumGray)
                        marginBottom(8.px)
                    }
                }
            ) {
                Text("📍 ${project.location}")
            }

            P(
                attrs = {
                    style {
                        fontSize(14.px)
                        color(HLDITheme.Colors.text)
                        lineHeight("1.6")
                        marginBottom(16.px)
                    }
                }
            ) {
                Text(project.description)
            }

            Div(
                attrs = {
                    style {
                        padding(12.px)
                        backgroundColor(HLDITheme.Colors.accent)
                        borderRadius(8.px)
                        textAlign(TextAlign.Center)
                        fontWeight(600)
                        color(HLDITheme.Colors.primary)
                        fontSize(14.px)
                    }
                }
            ) {
                Text(project.impact)
            }
        }
    }
}

// ============================================================================
// COMPONENT
// ============================================================================

@Composable
fun TeamCard(
    member: TeamMember,
    onViewProfile: (TeamMember) -> Unit
) {

    Div(
        attrs = TeamCardStyle.toAttrs {

            style {
                cursor("pointer")
            }

            onClick {
                onViewProfile(member)
            }

        }
    ) {

        // IMAGE
        Div(
            attrs = TeamCardImageWrapperStyle.toAttrs()
        ) {
            Img(
                src = member.imageUrl,
                alt = member.name,
                attrs = TeamCardImageStyle.toAttrs()
            )
        }


        // CONTENT
        Column(
            modifier = Modifier
                .padding(24.px)
                .then(TeamCardContentStyle.toModifier()),

            verticalArrangement = Arrangement.spacedBy(4.px)
        ) {

            H3(
                attrs = TeamCardNameStyle.toAttrs()
            ) {
                Text(member.name)
            }

            Div(
                attrs = TeamCardRoleStyle.toAttrs()
            ) {
                Text(member.role)
            }

            Div(
                attrs = TeamCardRoleUnderlineStyle.toAttrs {
                    classes("team-card-role-underline")
                }
            )

            if (member.isLeadership) {

                P(
                    attrs = TeamCardBioStyle.toAttrs {
                        style {
                            marginTop(10.px)
                        }
                    }
                ) {
                    Text(member.bio.shortBio())
                }

            }


            // SOCIAL BUTTONS
            if (
                member.email.isNotEmpty() ||
                member.facebook.isNotEmpty() ||
                member.instagram.isNotEmpty()
            ) {

                Div(
                    attrs = Modifier
                        .margin(top = 14.px)
                        .toAttrs {

                            // Prevent a social link click from also
                            // triggering the card's onViewProfile
                            onClick {
                                it.stopPropagation()
                            }

                        }
                ) {

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.px)
                    ) {

                        if (member.email.isNotEmpty()) {
                            SocialButton(
                                text = "Email",
                                link = "mailto:${member.email}"
                            )
                        }

                        if (member.facebook.isNotEmpty()) {
                            SocialButton(
                                text = "Facebook",
                                link = member.facebook
                            )
                        }

                        if (member.instagram.isNotEmpty()) {
                            SocialButton(
                                text = "Instagram",
                                link = member.instagram
                            )
                        }

                    }

                }

            }


            Button(
                modifier = Modifier
                    .margin(top = 16.px)
                    .padding(10.px, 18.px)
                    .then(ViewProfileButtonStyle.toModifier()),

                onClick = {
                    // stopPropagation not required here since
                    // clicking the button already calls the same
                    // handler as the card itself
                    onViewProfile(member)
                }
            ) {
                Text("View Profile")
            }

        }

    }

}
@Composable
fun BlogCard(post: BlogPost) {
    Div(
        attrs = Modifier.then(
            BlogCardStyle.toModifier()
        ).toAttrs()


    ) {
        // Image
        Div(
            attrs = {
                style {
                    height(200.px)
                    backgroundColor(HLDITheme.Colors.accent)
                    overflow(Overflow.Hidden)
                }
            }
        ) {
            Img(
                src = post.imageUrl,
                alt = post.title,
                attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        objectFit(ObjectFit.Cover)
                    }
                }
            )
        }

        // Content
        Div(
            attrs = {
                style {
                    padding(24.px)
                }
            }
        ) {
            // Category
            Span(
                attrs = {
                    style {
                        display(DisplayStyle.InlineBlock)
                        padding(4.px, 12.px)
                        backgroundColor(HLDITheme.Colors.accent)
                        color(HLDITheme.Colors.primary)
                        borderRadius(16.px)
                        fontSize(12.px)
                        fontWeight(600)
                        marginBottom(12.px)
                    }
                }
            ) {
                Text(post.category)
            }

            H3(
                attrs = {
                    style {
                        textAlign(TextAlign.Center)
                        fontSize(18.px)
                        color(HLDITheme.Colors.text)
                        marginBottom(8.px)
                        fontFamily("Playfair Display", "serif")
                    }
                }
            ) {
                Text(post.title)
            }

            P(
                attrs = {
                    style {
                        textAlign(TextAlign.Center)
                        fontSize(14.px)
                        color(HLDITheme.Colors.mediumGray)
                        lineHeight("1.6")
                        marginBottom(12.px)
                    }
                }
            ) {
                Text(post.content)
            }

            Div(
                attrs = {
                    style {
                        fontSize(12.px)
                        color(HLDITheme.Colors.mediumGray)
                    }
                }
            ) {
                Text("${post.author} • ${post.date}")
            }
        }
    }
}

@Composable
fun EventCard(event: Event) {
    Div(
        attrs = {
            style {
                backgroundColor(HLDITheme.Colors.white)
                borderRadius(12.px)
                padding(24.px)
                boxShadow(0.px, 4.px, 12.px, color = Color.rgba(0, 0, 0, 0.1f))
                borderLeft(4.px, LineStyle.Dotted, HLDITheme.Colors.primary)
               /*
                transition("all 0.3s ease")
                styleModifier {
                    add("&:hover") {
                        put("transform", "translateX(4px)")
                        put("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.15)")
                    }
                }

                */
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    fontSize(14.px)
                    color(HLDITheme.Colors.secondary)
                    fontWeight(600)
                    marginBottom(8.px)
                }
            }
        ) {
            Text(event.date)
        }

        H3(
            attrs = {
                style {
                    fontSize(20.px)
                    color(HLDITheme.Colors.text)
                    marginBottom(8.px)
                    fontFamily("Playfair Display", "serif")
                }
            }
        ) {
            Text(event.title)
        }

        Div(
            attrs = {
                style {
                    fontSize(14.px)
                    color(HLDITheme.Colors.mediumGray)
                    marginBottom(8.px)
                }
            }
        ) {
            Text("📍 ${event.location}")
        }

        P(
            attrs = {
                style {
                    fontSize(14.px)
                    color(HLDITheme.Colors.text)
                    lineHeight("1.6")
                    marginBottom(16.px)
                }
            }
        ) {
            Text(event.description)
        }

        GradientButton(
            text = "Register Now",
            href = event.registrationUrl
        )
    }
}